import Model from 'src/app/core/models/model';

/**
 * @model CultureCategoryModel
 * @description
 * Represents the data structure for Culture Categories within the AquaCloud application.
 * This model serves as a high-level classification entity for different types of aquaculture operations.
 *
 * **Domain Context:**
 * Aquaculture is diverse, ranging from Shrimp farming to Fish farming, and further divided into stages
 * like Hatchery (breeding), Nursery (early growth), and Grow-out (fattening).
 * A "Culture Category" defines this primary mode of operation.
 *
 * **Purpose:**
 * To provide a standardized way to categorize farms and ponds. This categorization is foundational
 * because it dictates:
 * 1. **Species Selection**: A "Shrimp" category limits species to Vannamei, Monodon, etc.
 * 2. **Data Collection**: Different categories require different metrics (e.g., Hatcheries track larval stages, Grow-out tracks biomass).
 * 3. **Reporting**: Financial and biological reports are often aggregated by culture category.
 *
 * **Key Responsibilities:**
 * 1. **Classification**: Stores the descriptive `name` of the culture type.
 * 2. **Identification**: May include an `abbreviation` for concise display in tables or codes.
 * 3. **Filtering**: Acts as a primary filter for dashboards and reports.
 *
 * **Data Flow:**
 * - **Definition**: Master data managed by System Administrators.
 * - **Retrieval**: Fetched via `CultureCategoryService` during Farm or Pond creation.
 * - **Usage**:
 *   - **Farm Setup**: User selects "Shrimp Culture".
 *   - **Pond Creation**: Inherits or allows selection of specific culture types based on this category.
 *
 * **User Roles & Permissions:**
 * - **System Admin**: Can create/edit categories.
 * - **Farmer/Manager**: Selects these categories to define their farm's purpose.
 *
 * **Features:**
 * - Simple reference model extending the base `Model`.
 * - Supports optional abbreviations for UI optimization.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class CultureCategoryModel extends Model {
  /**
   * @type {string}
   * @description
   * The human-readable name of the culture category.
   * Examples: "Shrimp", "Fish", "Crab", "Hatchery".
   */
  name: string;

  /**
   * @type {string}
   * @description
   * A short code or abbreviation for the category.
   * Useful for generating unique IDs (e.g., "SHR-001") or displaying in compact UI columns.
   * @optional
   * @example "SHR", "FSH"
   */
  abbreviation?: string;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the CultureCategoryModel.
   * Calls the parent `Model` constructor to initialize base properties like ID and timestamps.
   */
  constructor() {
    super();
  }
}
